import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Clock3, Search, X } from "lucide-react";
import Seo, { breadcrumbSchema, faqSchema } from "../components/Seo";
import { Reveal, Eyebrow, PageHero, CtaBand, Crumbs, FaqList } from "../components/Shared";
import { ARTICLES, CATEGORIES } from "../data/articles";
import { IMG, SITE } from "../data/mock";


const blogFaqs = [
  { q: "Who writes the Iron Grit blog?", a: "Every article is written by the Boston Iron Grit coaching staff — led by owner and head coach Tina Ramos — and reviewed by a second credentialed coach before publishing." },
  { q: "What topics does the blog cover?", a: "Practical training and health guides: starting CrossFit, training frequency, strength over 50, HYROX preparation, nutrition, body composition, visceral fat, Zone 2 training and longevity topics like peptides and GLP-1s." },
  { q: "How often are new articles published?", a: "We publish new coaching articles regularly, alternating between beginner training guides and health and longevity topics tied to the Live B.I.G. 365 program." },
];

export default function Blog() {
  const [cat, setCat] = useState("All");
  const [q, setQ] = useState("");
  const query = q.trim().toLowerCase();
  const matches = (a) => {
    const hay = [
      a.title, a.excerpt, a.category, a.author,
      ...(a.sections || []).flatMap((s) => [s.h2, ...(s.paras || []), ...(s.list || [])]),
      ...(a.faqs || []).flatMap((f) => [f.q, f.a]),
    ].join(" ").toLowerCase();
    return query.split(/\s+/).filter(Boolean).every((w) => hay.includes(w));
  };
  const base = cat === "All" ? ARTICLES : ARTICLES.filter((a) => a.category === cat);
  const list = query ? base.filter(matches) : base;
  const hub = ARTICLES.find((a) => a.hub);
  const rest = list.filter((a) => !(cat === "All" && a.hub && !query));

  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Blog", path: "/blog" },
  ];

  return (
    <>
      <Seo
        title="Training & Nutrition Blog | Boston Iron Grit"
        description="Practical guides from the Boston Iron Grit coaching team: starting CrossFit, training frequency, weightlifting, strength over 50, HYROX and nutrition."
        path="/blog"
        schemas={[
          breadcrumbSchema(crumbs),
          {
            "@context": "https://schema.org",
            "@type": "ItemList",
            "@id": `${SITE.url}/blog#articles`,
            itemListElement: ARTICLES.map((a, i) => ({ "@type": "ListItem", position: i + 1, url: `${SITE.url}/blog/${a.slug}`, name: a.title })),
          },
          faqSchema(blogFaqs),
        ]}
      />
      <PageHero
        label="The Blog"
        title={<>Coaching wisdom,<br /><span className="text-neutral-500">written down.</span></>}
        sub="No fluff, no fads. Practical guides from the coaching floor — written by the coaches you'll actually train with, reviewed by their peers."
        image={IMG.chalk}
      />

      <section className="bg-[#0A0A0B] pb-6">
        <div className="max-w-7xl mx-auto px-6">
          <Crumbs items={crumbs} />
        </div>
      </section>

      {/* Search + category filter */}
      <section className="bg-[#0A0A0B] py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="relative max-w-xl mb-6">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500 pointer-events-none" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="blog-search-input"
              placeholder="Search articles — protein, zone 2, beginners, HYROX…"
              className="w-full bg-[#121214] border border-white/10 focus:border-[#9D0423] focus:outline-none focus:ring-1 focus:ring-[#9D0423]/40 text-white placeholder:text-neutral-600 pl-11 pr-11 py-3.5 text-sm transition-colors"
            />
            {q && (
              <button
                onClick={() => setQ("")}
                aria-label="Clear search"
                data-testid="blog-search-clear"
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-neutral-500 hover:text-white transition-colors"
              >
                <X size={16} />
              </button>
            )}
          </div>
          {query && (
            <p className="mb-5 font-mono text-[11px] uppercase tracking-[0.16em] text-neutral-500" data-testid="blog-search-count">
              {list.length === 0 ? "No articles match" : `${list.length} article${list.length > 1 ? "s" : ""} match`} “{q.trim()}”
            </p>
          )}
          <div className="flex flex-wrap gap-2">
          {["All", ...CATEGORIES].map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              data-testid={`blog-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={`px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.14em] border transition-all duration-200 ${
                c === cat
                  ? "bg-[#9D0423] border-[#9D0423] text-white"
                  : "border-white/10 text-neutral-400 hover:border-white/30 hover:text-white"
              }`}
            >
              {c}
            </button>
          ))}
          </div>
        </div>
      </section>

      {/* Featured hub article */}
      {cat === "All" && hub && !query && (
        <section className="bg-[#0A0A0B] pb-10">
          <div className="max-w-7xl mx-auto px-6">
            <Reveal>
              <Link to={`/blog/${hub.slug}`} className="group grid lg:grid-cols-2 bg-[#121214] border border-white/8 hover:border-[#9D0423]/50 transition-colors overflow-hidden">
                <div className="relative h-72 lg:h-auto overflow-hidden">
                  <img src={hub.image} alt={hub.title} style={{ objectPosition: hub.imagePos || "center 30%" }} className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-700" loading="lazy" decoding="async" />
                  <div className="absolute top-5 left-5">
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white bg-[#9D0423] px-3 py-1.5">Start Here · The Hub Guide</span>
                  </div>
                </div>
                <div className="p-8 lg:p-12 flex flex-col justify-center">
                  <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#E56A79]">{hub.category}</p>
                  <h2 className="mt-3 font-heading font-extrabold uppercase text-2xl lg:text-4xl text-white tracking-tight leading-tight group-hover:text-[#E56A79] transition-colors">
                    {hub.title}
                  </h2>
                  <p className="mt-4 text-neutral-400 text-[15px] leading-relaxed">{hub.excerpt}</p>
                  <div className="mt-6 flex items-center gap-4 font-mono text-[11px] uppercase tracking-[0.16em] text-neutral-500">
                    <span>{hub.author}</span>
                    <span className="text-neutral-700">/</span>
                    <span className="flex items-center gap-1.5"><Clock3 size={12} /> {hub.readTime}</span>
                  </div>
                </div>
              </Link>
            </Reveal>
          </div>
        </section>
      )}

      {/* Article grid */}
      <section className="bg-[#0A0A0B] pb-20">
        <div className="max-w-7xl mx-auto px-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {rest.map((a, i) => (
            <Reveal key={a.slug} delay={(i % 3) * 80}>
              <Link to={`/blog/${a.slug}`} className="group flex flex-col bg-[#121214] border border-white/5 hover:border-white/20 transition-colors h-full overflow-hidden">
                <div className="relative h-52 overflow-hidden">
                  <img src={a.image} alt={a.title} style={{ objectPosition: a.imagePos || "center 30%" }} className="absolute inset-0 w-full h-full object-cover opacity-65 group-hover:opacity-85 group-hover:scale-105 transition-all duration-700" loading="lazy" decoding="async" />
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#E56A79]">{a.category}</p>
                  <h2 className="mt-2.5 font-heading font-bold uppercase text-lg text-white tracking-tight leading-snug group-hover:text-[#E56A79] transition-colors">
                    {a.title}
                  </h2>
                  <p className="mt-3 text-[13px] text-neutral-400 leading-relaxed flex-1">{a.excerpt}</p>
                  <div className="mt-5 flex items-center justify-between">
                    <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-neutral-500">{a.author} · {a.readTime}</span>
                    <ArrowUpRight size={15} className="text-[#9D0423] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#0D0D0F] border-y border-white/5 py-16" data-testid="blog-faq">
        <div className="max-w-4xl mx-auto px-6">
          <FaqList faqs={blogFaqs} title="About the Blog" />
        </div>
      </section>

      <CtaBand />
    </>
  );
}
